import { useEffect, useState, useRef } from 'react';
import { AlertTriangle, X, Navigation } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store';
import { setEmergencyLocation } from '../../store/slices/uiSlice';
import { API_BASE_URL } from '../../lib/utils';

interface EmergencyPayload {
  incidentId: string;
  resourceId: string;
  resourceName: string;
  location: { latitude: number; longitude: number; address: string };
}

interface IncidentLite {
  id: string;
  title: string;
  description?: string;
  reportedAt: string;
  location: { latitude: number; longitude: number; address: string };
}

export default function EmergencyAlert() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { token } = useAppSelector((state) => state.auth);
  const [emergency, setEmergency] = useState<EmergencyPayload | null>(null);
  const [visible, setVisible] = useState(false);
  const seenIdsRef = useRef<Set<string>>(new Set());
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!token) return;

    const checkForEmergencies = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/incidents`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const incidents: IncidentLite[] = data.incidents || [];

        // Find emergency incidents (titled "EMERGENCY - ...")
        const emergencies = incidents.filter((i) => i.title?.startsWith('EMERGENCY'));

        // First load: just record existing emergencies, don't alert
        if (!initializedRef.current) {
          emergencies.forEach((e) => seenIdsRef.current.add(e.id));
          initializedRef.current = true;
          return;
        }

        // Find new emergencies since last check
        const newEmergencies = emergencies.filter((e) => !seenIdsRef.current.has(e.id));
        newEmergencies.forEach((e) => seenIdsRef.current.add(e.id));

        // Only show alert for the most recent new emergency
        if (newEmergencies.length > 0) {
          const latest = newEmergencies[0];
          // Extract resource name from title (e.g., "EMERGENCY - MD01 Urgent Assistance")
          const titleMatch = latest.title.match(/EMERGENCY\s*-\s*(\S+)/);
          const resourceName = titleMatch ? titleMatch[1] : 'Unknown';

          const payload: EmergencyPayload = {
            incidentId: latest.id,
            resourceId: resourceName,
            resourceName,
            location: latest.location,
          };

          setEmergency(payload);
          setVisible(true);
          playEmergencyTone();
          dispatch(setEmergencyLocation({ latitude: latest.location.latitude, longitude: latest.location.longitude }));
          localStorage.setItem('c2_emergency_location', JSON.stringify({
            latitude: latest.location.latitude,
            longitude: latest.location.longitude,
            timestamp: Date.now(),
          }));
        }
      } catch { /* ignore */ }
    };

    checkForEmergencies();
    const interval = setInterval(checkForEmergencies, 5000);
    return () => clearInterval(interval);
  }, [token, dispatch]);

  const playEmergencyTone = () => {
    try {
      const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();

      // Star Trek Voyager-style "Red Alert" klaxon:
      // A sweeping two-tone whoop alternating between high and low frequencies,
      // played as a layered sawtooth + square wave for that synthetic urgency.
      const playWhoop = (startTime: number, lowFreq: number, highFreq: number, duration: number) => {
        // Primary sawtooth oscillator (main siren)
        const osc1 = ctx.createOscillator();
        osc1.type = 'sawtooth';
        const gain1 = ctx.createGain();
        osc1.connect(gain1);
        gain1.connect(ctx.destination);

        // Secondary square oscillator (harmonic edge)
        const osc2 = ctx.createOscillator();
        osc2.type = 'square';
        const gain2 = ctx.createGain();
        osc2.connect(gain2);
        gain2.connect(ctx.destination);

        // Frequency sweep: low -> high -> low (whoop)
        osc1.frequency.setValueAtTime(lowFreq, startTime);
        osc1.frequency.linearRampToValueAtTime(highFreq, startTime + duration * 0.5);
        osc1.frequency.linearRampToValueAtTime(lowFreq, startTime + duration);

        osc2.frequency.setValueAtTime(lowFreq * 1.5, startTime);
        osc2.frequency.linearRampToValueAtTime(highFreq * 1.5, startTime + duration * 0.5);
        osc2.frequency.linearRampToValueAtTime(lowFreq * 1.5, startTime + duration);

        // Envelope: quick attack, sustain, quick release
        gain1.gain.setValueAtTime(0, startTime);
        gain1.gain.linearRampToValueAtTime(0.25, startTime + 0.02);
        gain1.gain.setValueAtTime(0.25, startTime + duration - 0.05);
        gain1.gain.linearRampToValueAtTime(0, startTime + duration);

        gain2.gain.setValueAtTime(0, startTime);
        gain2.gain.linearRampToValueAtTime(0.08, startTime + 0.02);
        gain2.gain.setValueAtTime(0.08, startTime + duration - 0.05);
        gain2.gain.linearRampToValueAtTime(0, startTime + duration);

        osc1.start(startTime);
        osc1.stop(startTime + duration);
        osc2.start(startTime);
        osc2.stop(startTime + duration);
      };

      const now = ctx.currentTime;
      const whoopDuration = 0.5;
      const gap = 0.08;

      // 4 sweeping whoops — classic Red Alert pattern
      for (let i = 0; i < 4; i++) {
        const t = now + i * (whoopDuration + gap);
        playWhoop(t, 330, 660, whoopDuration); // Low A4 -> E5 -> A4
      }

      // Auto-close audio context after sound finishes
      setTimeout(() => {
        try { ctx.close(); } catch { /* ignore */ }
      }, (whoopDuration + gap) * 4 * 1000 + 500);
    } catch {
      // Audio not available
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(() => setEmergency(null), 300);
  };

  const handleViewIncident = () => {
    if (emergency) {
      navigate(`/workspace/incidents/${emergency.incidentId}`);
      setVisible(false);
      setTimeout(() => setEmergency(null), 300);
    }
  };

  if (!visible || !emergency) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-red-500/95 backdrop-blur-md animate-in fade-in duration-300">
      <div className="max-w-md w-full mx-4">
        <div className="bg-card border-2 border-red-500 rounded-2xl overflow-hidden shadow-2xl">
          {/* Header */}
          <div className="bg-red-500 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-white animate-pulse" />
              <div>
                <h2 className="text-xl font-extrabold text-white">EMERGENCY ACTIVATED</h2>
                <p className="text-red-100 text-sm">Urgent assistance requested</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="w-8 h-8 rounded-lg bg-red-400/20 hover:bg-red-400/30 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                <Navigation className="w-6 h-6 text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white">{emergency.resourceName}</p>
                <p className="text-xs text-white/60 truncate">{emergency.location.address}</p>
              </div>
            </div>

            <div className="text-center">
              <p className="text-sm text-white/80">
                Incident <span className="font-mono font-bold text-white">{emergency.incidentId}</span> has been created
              </p>
              <p className="text-xs text-white/50 mt-1">Resource has been automatically assigned</p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={handleDismiss}
                className="flex-1 py-3 rounded-xl bg-white/10 border border-white/10 text-white font-bold text-sm hover:bg-white/15 transition-colors"
              >
                Dismiss
              </button>
              <button
                onClick={handleViewIncident}
                className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold text-sm hover:bg-red-400 transition-colors flex items-center justify-center gap-2"
              >
                <Navigation className="w-4 h-4" />
                View Incident
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
